import { useEffect, useMemo, useState } from "react";
import { TMapRef } from "../map-asset/type";
import { tripKeyFor } from "../map-asset/multiDay/renderMultiVehicleRoutes";
import { VehicleGroup } from "./useMultiVehicleTripData";

// Below this, putting a route on the road moves it by a fraction of a pixel.
// A whole-fleet view spans thousands of miles, so the correction is invisible
// and not worth a paid lookup; from about here in it starts to show.
export const ROAD_DETAIL_ZOOM = 14;

// even zoomed in, one screen shouldn't kick off an unbounded pile of calls
const MAX_VISIBLE_TRIPS = 25;

// sampling the path is enough to decide "is any of this on screen" - walking
// every fix of every trip on each map idle is wasted work
const BOUNDS_SAMPLE_STRIDE = 10;

type TripBox = {
  tripKey: string;
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
};

// Which trips are close enough to look at that road alignment would show.
// Everything else stays on its raw fixes.
export const useVisibleTripKeys = ({
  mapRef,
  vehicleGroups,
  selectedTripKeys,
  isMapReady,
  enabled,
}: {
  mapRef: React.MutableRefObject<TMapRef>;
  vehicleGroups: VehicleGroup[];
  selectedTripKeys: Set<string>;
  isMapReady: boolean;
  // off for small selections, where every route is aligned up front anyway
  enabled: boolean;
}) => {
  const [visibleTripKeys, setVisibleTripKeys] = useState<string[]>([]);

  // one box per trip, so an idle only has to test rectangles
  const tripBoxes = useMemo(() => {
    const boxes: TripBox[] = [];
    if (!enabled) return boxes;
    vehicleGroups.forEach((group) => {
      group.trips.forEach((points, tripIndex) => {
        const tripKey = tripKeyFor(group.deviceId, tripIndex);
        if (!selectedTripKeys.has(tripKey) || points.length === 0) return;
        let minLat = Infinity;
        let maxLat = -Infinity;
        let minLng = Infinity;
        let maxLng = -Infinity;
        for (let i = 0; i < points.length; i += BOUNDS_SAMPLE_STRIDE) {
          const point = points[i];
          if (point.latitude < minLat) minLat = point.latitude;
          if (point.latitude > maxLat) maxLat = point.latitude;
          if (point.longitude < minLng) minLng = point.longitude;
          if (point.longitude > maxLng) maxLng = point.longitude;
        }
        boxes.push({ tripKey, minLat, maxLat, minLng, maxLng });
      });
    });
    return boxes;
  }, [vehicleGroups, selectedTripKeys, enabled]);

  useEffect(() => {
    const map = mapRef.current?.map;
    const google = (window as any).google;
    if (!map || !google || !isMapReady || !enabled) {
      setVisibleTripKeys((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const recalculate = () => {
      if ((map.getZoom() ?? 0) < ROAD_DETAIL_ZOOM) {
        setVisibleTripKeys((prev) => (prev.length === 0 ? prev : []));
        return;
      }
      const bounds = map.getBounds();
      if (!bounds) return;

      const southWest = bounds.getSouthWest();
      const northEast = bounds.getNorthEast();
      const minLat = southWest.lat();
      const maxLat = northEast.lat();
      const minLng = southWest.lng();
      const maxLng = northEast.lng();

      const keys: string[] = [];
      for (const box of tripBoxes) {
        if (keys.length >= MAX_VISIBLE_TRIPS) break;
        const overlaps =
          box.maxLat >= minLat &&
          box.minLat <= maxLat &&
          box.maxLng >= minLng &&
          box.minLng <= maxLng;
        if (overlaps) keys.push(box.tripKey);
      }

      // same set, same array - a new identity would restart the resolver
      setVisibleTripKeys((prev) =>
        prev.length === keys.length && prev.every((key, i) => key === keys[i])
          ? prev
          : keys
      );
    };

    recalculate();
    const listener = google.maps.event.addListener(map, "idle", recalculate);
    return () => google.maps.event.removeListener(listener);
  }, [mapRef, tripBoxes, isMapReady, enabled]);

  return visibleTripKeys;
};
